import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

export default function ExchangeDealerShow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dealer, setDealer] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDealer();
    }, [id]);

    const loadDealer = async () => {
        try {
            const response = await api.get(`/exchange_dealers/${id}`);
            setDealer(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading exchange dealer:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Are you sure you want to delete this exchange dealer?')) return;

        try {
            await api.delete(`/exchange_dealers/${id}`);
            navigate('/exchange_dealers');
        } catch (error) {
            alert('Error deleting dealer. It may be in use.');
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!dealer) return <div className="p-8">Exchange dealer not found.</div>;

    return (
        <div className="p-8 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">{dealer.dealer_name}</h1>
                    <p className="text-slate-600 mt-1">Exchange Dealer Details</p>
                </div>
                <div className="flex gap-3">
                    <Link
                        to={`/exchange_dealers/${id}/edit`}
                        className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={handleDelete}
                        className="px-6 py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all"
                    >
                        Delete
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Dealer Name</label>
                        <p className="text-lg font-bold text-slate-900">{dealer.dealer_name}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Contact Person</label>
                        <p className="text-lg text-slate-700">{dealer.contact_person || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Phone</label>
                        <p className="text-lg text-slate-700">{dealer.phone || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Email</label>
                        <p className="text-lg text-slate-700">{dealer.email || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                        <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${dealer.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {dealer.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </div>

                    {dealer.address && (
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Address</label>
                            <p className="text-lg text-slate-700 whitespace-pre-wrap">{dealer.address}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-8">
                <Link
                    to="/exchange_dealers"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Exchange Dealers
                </Link>
            </div>
        </div>
    );
}
